"use client";

import Link from "next/link";
import CheckoutLink from "@/components/CheckoutLink";
import { getProductById, ProductId } from "@/lib/products";
import { trackDirectCheckout } from "@/lib/analytics/events";
import { LEMON_SQUEEZY } from "@/lib/analytics/config";

type BuyButtonProduct = ProductId | "bundle";

interface BuyButtonProps {
  className?: string;
  children?: React.ReactNode;
  source?: string;
  source_id?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  product?: BuyButtonProduct;
  variant?: "monthly" | "yearly" | "lifetime";
}

export default function BuyButton({
  className,
  children,
  source,
  source_id: _source_id, // eslint-disable-line @typescript-eslint/no-unused-vars
  target = "_self",
  product = "x-unfollow-pro", // Default to x-unfollow-pro for backward compatibility
  variant,
}: BuyButtonProps) {
  const handleClick = () => {
    // Track direct checkout flow (add to cart + begin checkout)
    trackDirectCheckout(
      {
        id: product,
        name: product,
        variant, // Track variant if available
      },
      source
    );
  };

  // Coming-soon products have no live Lemon Squeezy listing yet — render a
  // disabled CTA that routes to the product page instead of letting users
  // click through to a broken checkout. Centralising the swap here means
  // every call site auto-handles the case without per-site edits.
  if (product !== "bundle") {
    const productData = getProductById(product);
    if (productData?.comingSoon) {
      return (
        <Link
          href={`/product/${productData.slug}/`}
          className={`cursor-default opacity-80 hover:opacity-100 ${className || ""}`}
          aria-label={`${productData.alternateName} is coming soon — open product page`}
        >
          Coming Soon
        </Link>
      );
    }
  }

  // Resolve checkout URL
  let checkoutUrl = "";
  if (product === "bundle") {
    checkoutUrl = LEMON_SQUEEZY.BUNDLE_CHECKOUT_URL;
  } else {
    // It's a ProductId, look it up in the centralized data
    const productData = getProductById(product);
    if (productData) {
      if (variant && productData.offers?.[variant]) {
        checkoutUrl = productData.offers[variant].url;
      } else if (productData.offers?.lifetime?.url) {
        checkoutUrl = productData.offers.lifetime.url;
      } else {
        console.warn(
          `BuyButton: No checkout URL found for product "${product}" variant "${variant}"`
        );
      }
    }
  }

  let fullUrl = checkoutUrl;
  if (checkoutUrl) {
    const url = new URL(checkoutUrl);
    url.searchParams.set("embed", "1");
    if (source) url.searchParams.set("source", source);
    fullUrl = url.toString();
  }

  return (
    <CheckoutLink
      href={fullUrl}
      className={className}
      onClick={handleClick}
      target={target}
      rel="nofollow sponsored noopener noreferrer"
    >
      {children || "Buy Lifetime Pro License"}
    </CheckoutLink>
  );
}
